import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { FileText, Users, MessageSquare, DollarSign, Search as SearchIcon, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const EngagementTools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    FileText: FileText,
    Users: Users,
    MessageSquare: MessageSquare,
    DollarSign: DollarSign,
    Search: SearchIcon
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${API}/engagement-tools`);
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Citizen Engagement Tools
          </h1>
          <p className="text-lg text-slate-600">
            Step-by-step guides to help you participate actively in the legislative process
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((tool, index) => {
            const IconComponent = iconMap[tool.icon] || FileText;
            
            return (
              <Card 
                key={tool.id} 
                className="shadow-sm hover:shadow-md transition-shadow"
                data-testid={`tool-card-${index}`}
              >
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                      <IconComponent className="h-6 w-6 text-primary-700" />
                    </div>
                    <CardTitle className="text-xl">{tool.title}</CardTitle>
                  </div>
                  <CardDescription className="text-base">
                    {tool.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible>
                    <AccordionItem value="steps" className="border-none">
                      <AccordionTrigger className="text-primary-700 font-medium hover:no-underline">
                        View Steps
                      </AccordionTrigger>
                      <AccordionContent>
                        <ol className="space-y-3 mt-2">
                          {tool.steps.map((step, stepIndex) => (
                            <li key={stepIndex} className="flex items-start space-x-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-primary-700 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                {stepIndex + 1}
                              </span>
                              <span className="text-slate-600 leading-relaxed">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EngagementTools;
